import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

export interface AuthLayoutProps {
  title: string
  subtitle: string
  children: ReactNode
  /** Footer links row below the form. */
  footer?: ReactNode
}

/**
 * Split layout shared by the public auth screens: dark Porcelain stage panel
 * (brand + editorial type) on the left, form card on the right. Collapses to
 * a single form column on small viewports.
 */
export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* Brand stage */}
      <section className="relative hidden flex-col justify-between overflow-hidden bg-stage p-12 lg:flex">
        <Link to="/" className="inline-flex items-baseline gap-2">
          <span className="font-display text-2xl font-semibold tracking-tight text-stage-text">nMach</span>
          <span className="rounded-full border border-cobalt-light/40 px-2.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-widest text-cobalt-light">
            Portal
          </span>
        </Link>

        <div className="flex flex-col gap-6">
          <p className="text-small font-medium uppercase tracking-widest text-cobalt-light">Client workspace</p>
          <h1 className="max-w-md font-display text-display-l text-stage-text">
            Everything for your nMach work, in one quiet place.
          </h1>
          <p className="max-w-md text-body text-stage-muted">
            Downloads, agreements, billing and account access — built with the same care we put into the
            products themselves.
          </p>
        </div>

        <div className="flex items-center gap-3 text-small text-stage-muted" aria-hidden="true">
          <span className="h-px w-10 bg-line-dark" />
          Porcelain Studio
        </div>
        {/* Decorative geometry */}
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full border border-line-dark"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-cobalt/10 blur-2xl"
          aria-hidden="true"
        />
      </section>

      {/* Form column */}
      <section className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mx-auto flex w-full max-w-md flex-col gap-8">
          <header className="flex flex-col gap-2">
            <Link to="/" className="lg:hidden">
              <span className="font-display text-2xl font-semibold tracking-tight text-ink">nMach</span>
            </Link>
            <h2 className="font-display text-display-m text-ink">{title}</h2>
            <p className="text-body text-ink-muted">{subtitle}</p>
          </header>

          {children}

          {footer && <div className="flex flex-col gap-3 text-small text-ink-muted">{footer}</div>}
        </div>
      </section>
    </div>
  )
}
